import { z } from "zod";
import {
  publicCampusSchema,
  type PublicCampus,
  type PublicEvent,
  type PublicGroup,
  type PublicSite,
} from "./public-site";

/*
 * The public campus pages (ADR 0036 amendment, Milestone 5 B1): the location chooser at /campuses and a page per
 * campus at /campuses/<slug>, as Church Center's campus filter lets a visitor pick where they are going. Served from
 * `get_public_campus(hostname, slug)`: with a slug, the site envelope with the campus on it; without, the envelope
 * alone, from which the chooser lists the campuses the church marked as listed. An unlisted campus still has its
 * page, by link, as an unlisted group does.
 */

export const campusPaths = {
  chooser: "/campuses",
  campus: (slug: string): string => `/campuses/${slug}`,
} as const;

/** A path under /campuses, as `requestPath` gives it (no slashes at the ends). */
export function isCampusPath(path: string): boolean {
  return path === "campuses" || /^campuses\/[a-z0-9-]+$/.test(path);
}

/** The slug a campus path names, or null for the chooser. Not a campus path: undefined. */
export function campusSlugOf(path: string): string | null | undefined {
  if (!isCampusPath(path)) return undefined;
  return path === "campuses" ? null : path.slice("campuses/".length);
}

export type CampusView =
  /** Every listed campus, for a visitor choosing where to go. */
  | { readonly kind: "chooser"; readonly campuses: readonly PublicCampus[] }
  /** One campus, with the public events at it and those open to every campus. */
  | {
      readonly kind: "campus";
      readonly campus: PublicCampus;
      readonly events: readonly PublicEvent[];
      /** The public groups that meet at this campus or at no campus. */
      readonly groups: readonly PublicGroup[];
      /** The other listed campuses, so a visitor at the wrong one finds the right one. */
      readonly others: readonly PublicCampus[];
    };

const campusEnvelopeSchema = z.object({ campus: z.unknown().optional() });

/** The campus `get_public_campus` put on the envelope, or null for the chooser or a campus that fails its contract. */
export function parseCampusPage(raw: unknown): PublicCampus | null {
  const envelope = campusEnvelopeSchema.safeParse(raw);
  if (!envelope.success || envelope.data.campus === undefined) return null;
  const campus = publicCampusSchema.safeParse(envelope.data.campus);
  return campus.success ? campus.data : null;
}

/** The events a campus's page shows: at this campus, or at no campus (open to all, ADR 0036); soonest first. */
export function campusEvents(events: readonly PublicEvent[], campus: PublicCampus): PublicEvent[] {
  return events
    .filter((e) => e.campus === undefined || e.campus.slug === campus.slug)
    .filter((e) => e.occurrences.length > 0)
    .sort((a, b) =>
      (a.occurrences[0]?.startsAt ?? "").localeCompare(b.occurrences[0]?.startsAt ?? ""),
    );
}

/** The groups a campus's page shows: meeting at this campus, or at no campus (open to all). */
export function campusGroups(groups: readonly PublicGroup[], campus: PublicCampus): PublicGroup[] {
  return groups.filter((g) => g.campus === undefined || g.campus.slug === campus.slug);
}

/** The listed campuses, in the church's order. */
export function listedCampuses(site: Pick<PublicSite, "campuses">): PublicCampus[] {
  return site.campuses.filter((c) => c.isListed);
}

/**
 * What the campus pages show for this site and address. The chooser needs two or more listed campuses: a church with
 * one has nothing to choose, and keeps whatever page of its own it may have at /campuses. A campus page is drawn for
 * the campus the database returned (listed or not).
 */
export function campusView(
  site: Pick<PublicSite, "campuses" | "events" | "groups">,
  campus: PublicCampus | null,
  slug: string | null,
): CampusView | null {
  if (slug === null) {
    const listed = listedCampuses(site);
    return listed.length >= 2 ? { kind: "chooser", campuses: listed } : null;
  }
  if (campus === null) return null;
  return {
    kind: "campus",
    campus,
    events: campusEvents(site.events, campus),
    groups: campusGroups(site.groups, campus),
    others: listedCampuses(site).filter((c) => c.slug !== campus.slug),
  };
}
